import React, { useEffect, useRef, useState } from 'react';
import Otto from './Otto';
import OttoAlive from './OttoAlive';
import { OTTO_SEASONS } from './ottoSeasons';
import OttoLoading from './OttoLoading';
import OttoInlineLoading from './OttoInlineLoading';
import OttoAward from './OttoAward';
import OttoIntroCard from './OttoIntroCard';
import BriefSendFlight from '../sendBriefFlight/BriefSendFlight';

// Dev-only Otto showcase (test environment only — gated by isTestEnv at
// the call site). Renders every pose in both wardrobes at hero /
// empty-state / chip sizes, plus live triggers for the interaction set:
// loading hover, send-brief fly-away, award celebration (± milestone),
// and the first-login intro card. Purely a QA surface — no app data.

const POSES = [
  { pose: 'default', label: 'Default' },
  { pose: 'waving', label: 'Waving' },
  { pose: 'searching', label: 'Searching' },
  { pose: 'writing', label: 'Writing' },
  { pose: 'flying', label: 'Flying / send' },
  { pose: 'sleeping', label: 'All caught up' },
  { pose: 'celebrating', label: 'Celebrating' },
];
const CAPTAIN_POSES = new Set(['default', 'waving', 'celebrating']);

const cellCls = 'flex flex-col items-center gap-2 bg-canvas border border-border-subtle rounded-lg p-3';
const capCls = "font-['Metropolis'] text-[10px] font-semibold tracking-[0.12em] uppercase text-ink-tertiary";
const btnCls = "fusion-btn-primary text-sm";
const btn2Cls = "font-['Metropolis'] text-sm font-semibold bg-surface-card text-ink-primary border border-[#CFD2C8] rounded-[10px] h-10 px-[18px] hover:border-ink-secondary transition-colors";

export default function OttoGallery({ defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [award, setAward] = useState({ show: false, label: null });
  const [showIntro, setShowIntro] = useState(false);
  const [flight, setFlight] = useState(null);
  const [loadingDemo, setLoadingDemo] = useState(false);
  const sendBtnRef = useRef(null);
  const fakeReqRef = useRef(null);

  const fireSend = () => {
    const r = sendBtnRef.current?.getBoundingClientRect();
    setFlight(r ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : null);
  };

  // Stand-in for a real request, so the head-cock demo exercises the same
  // active-goes-false path the app does rather than a canned playback.
  const fakeRequest = (ms) => {
    clearTimeout(fakeReqRef.current);
    setLoadingDemo(true);
    fakeReqRef.current = setTimeout(() => setLoadingDemo(false), ms);
  };

  useEffect(() => () => clearTimeout(fakeReqRef.current), []);

  return (
    <div className="bg-surface-card border border-border-subtle rounded-card shadow-card p-6 mt-8">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between text-left bg-transparent border-0 cursor-pointer">
        <span className="flex items-center gap-2.5">
          <Otto pose="default" size={28} ariaLabel="" />
          <span className="font-['Metropolis'] text-[13px] font-extrabold tracking-[0.12em] uppercase text-ink-primary">
            Otto Gallery
          </span>
          <span className="fusion-pill bg-status-attention-bg text-status-attention-text">Test only</span>
        </span>
        <span className="font-['Metropolis'] text-sm font-semibold text-ink-secondary">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className="mt-5 space-y-6">
          {/* Every pose, both wardrobes */}
          <div>
            <p className={`${capCls} mb-2.5`}>Poses × wardrobe (hero 88px)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {POSES.map(({ pose, label }) => (
                <React.Fragment key={pose}>
                  <div className={cellCls}>
                    <Otto pose={pose} variant="everyday" size={88} ariaLabel="" />
                    <span className={capCls}>{label}</span>
                    <span className="font-['Metropolis'] text-[10px] text-ink-tertiary">Everyday</span>
                  </div>
                  <div className={cellCls}>
                    {CAPTAIN_POSES.has(pose) ? (
                      <>
                        <Otto pose={pose} variant="captain" size={88} ariaLabel="" />
                        <span className={capCls}>{label}</span>
                        <span className="font-['Metropolis'] text-[10px] text-fusion-teal">Captain</span>
                      </>
                    ) : (
                      <>
                        <Otto pose={pose} variant="everyday" size={88} ariaLabel="" />
                        <span className={capCls}>{label}</span>
                        <span className="font-['Metropolis'] text-[10px] text-ink-tertiary">Everyday only</span>
                      </>
                    )}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Size legibility check */}
          <div>
            <p className={`${capCls} mb-2.5`}>Size check — hero / empty-state (56) / chip (32)</p>
            <div className="flex flex-wrap items-end gap-6">
              {['default', 'celebrating'].map(pose => (
                ['everyday', 'captain'].map(variant => (
                  <div key={`${pose}-${variant}`} className={cellCls}>
                    <div className="flex items-end gap-3">
                      <Otto pose={pose} variant={variant} size={88} ariaLabel="" />
                      <Otto pose={pose} variant={variant} size={56} ariaLabel="" />
                      <Otto pose={pose} variant={variant} size={32} ariaLabel="" />
                    </div>
                    <span className={capCls}>{pose} · {variant}</span>
                  </div>
                ))
              ))}
            </div>
          </div>

          {/* Seasonal accessories (forced, everyday default) */}
          <div>
            <p className={`${capCls} mb-2.5`}>Seasonal accessories — everyday default (forced)</p>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {OTTO_SEASONS.map(s => (
                <div key={s.id} className={cellCls}>
                  <OttoAlive season={s.id} size={78} interactive={false} ariaLabel="" />
                  <span className={capCls}>{s.displayName}</span>
                </div>
              ))}
            </div>
            <p className="font-['Metropolis'] text-[11px] text-ink-tertiary mt-2">
              Live in-season on the intro-card Otto. Type <strong>OTTO</strong> anywhere to run the parade cycling all 10.
            </p>
          </div>

          {/* Loading */}
          <div>
            <p className={`${capCls} mb-2.5`}>Loading (stationary hover over teal bar)</p>
            <div className="bg-canvas border border-border-subtle rounded-lg">
              <OttoLoading size="large" message="Finding the right suppliers" />
            </div>
          </div>

          {/* Interaction triggers */}
          <div>
            <p className={`${capCls} mb-2.5`}>Trigger the animations</p>
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" className={btn2Cls} onClick={() => setAward({ show: true, label: null })}>
                Award (normal)
              </button>
              <button type="button" className={btnCls} onClick={() => setAward({ show: true, label: 'Your 50th brief awarded!' })}>
                Award (milestone)
              </button>
              <button ref={sendBtnRef} type="button" className={btn2Cls} onClick={fireSend}>
                Send-brief flight
              </button>
              <button type="button" className={btn2Cls} onClick={() => setShowIntro(v => !v)}>
                {showIntro ? 'Hide intro card' : 'Show intro card'}
              </button>
            </div>
            <p className="font-['Metropolis'] text-[11px] text-ink-tertiary mt-2">
              Award = Captain Otto + wing-tip confetti (milestone adds the label). Send = Everyday Otto flies off. All honour prefers-reduced-motion.
            </p>
          </div>

          {/* ROAMING SHOTS + BODY MOTIONS — the two registries, neither of
              which this page could reach until 2026-08-18.

              Worth being blunt about the limit: these fire against the
              GALLERY's furniture. Several shots resolve a live target — a card,
              a nav row, the gap above the user card — and against this page
              they will either look wrong or refuse outright. `shotlist` on a
              real page is still the way to review them; this is here so the
              registries are visible and demoable at all. */}
          <ShotBoard />

          {/* Idle micro-animations — the set the roaming home-shot draws from */}
          <div>
            <p className={`${capCls} mb-2.5`}>Idle animations — click Otto to step through the set</p>
            <div className="flex flex-wrap items-center gap-6">
              <div className={cellCls}>
                <OttoAlive size={88} interactive ariaLabel="" />
                <span className={capCls}>Click to cycle</span>
              </div>
              <p className="font-['Metropolis'] text-[11px] text-ink-tertiary max-w-[46ch]">
                Each click steps one motion: wave · blink · head-tilt · stretch · scratch · shiver · preen · goggle · perk · surprise · yawn · sneeze · head-cock · barrel-roll. The roaming home shot draws from a subset of these — the idle pool leaves out wave, perk, surprise, head-cock and the roll, which are reactions rather than idles.
              </p>
            </div>
          </div>

          {/* Roaming idle set — fires the real full-screen overlay */}
          <div>
            <p className={`${capCls} mb-2.5`}>Roaming idle set (full-screen overlay)</p>
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" className={btn2Cls} onClick={() => window.__ottoPlay?.('fly')}>01 · Fly up the sidebar</button>
              <button type="button" className={btn2Cls} onClick={() => window.__ottoPlay?.('glass')}>02 · Glass press</button>
              <button type="button" className={btn2Cls} onClick={() => window.__ottoPlay?.('peek')}>04 · Peer over a card</button>
              <button type="button" className={btn2Cls} onClick={() => window.__ottoPlay?.('perch')}>07 · Perch &amp; slip</button>
              <button type="button" className={btn2Cls} onClick={() => window.__ottoPlay?.('lean')}>03 · Lean &amp; wobble</button>
              <button type="button" className={btn2Cls} onClick={() => window.__ottoPlay?.('ledge')}>09 · Preen on the ledge</button>
              <button type="button" className={btn2Cls} onClick={() => window.__ottoPlay?.('nav')}>10 · Perch down the nav</button>
              <button type="button" className={btn2Cls} onClick={() => window.__ottoPlay?.('snag')}>08 · Wing caught</button>              <button type="button" className={btn2Cls} onClick={() => window.__ottoPlay?.('home')}>Home · body shot</button>
            </div>
            <p className="font-['Metropolis'] text-[11px] text-ink-tertiary mt-2">
              Fires the actual roaming overlay (admin pages only). Fly &amp; Home anchor to the sidebar's live position; Peer needs a <strong>.shadow-card</strong> on screen with ~90px clear above it; Wing caught needs <strong>two cards side by side</strong> with an 8–44px gap between them. In the app these are idle-scheduled after 20–45s of no activity. If nothing happens, there's no usable target on this screen.
            </p>
            <p className="font-['Metropolis'] text-[11px] text-ink-tertiary mt-1.5">
              <strong>03 Lean</strong> is the only shot that moves live UI — it wobbles the real nav rail by up to 18px, through a watchdogged transient class that force-clears on timeout, unmount and tab-hide. It suppresses itself if the rail has focus.
              {' '}<strong>09 Preen</strong> perches in the gap between the last nav item and the user card, and suppresses itself if that gap is under 95px, so a longer nav simply means he doesn't show up.
              {' '}<strong>10 Perch down the nav</strong> hops down the first three <em>Main-menu</em> rows (dipping each 2.5px as he lands) and suppresses if fewer than three are present — it never touches the System rows.
            </p>
          </div>

          {/* 05 · Head-cock — event-driven, so it needs a fake request rather
              than a trigger button that plays a fixed-length clip. */}
          <div>
            <p className={`${capCls} mb-2.5`}>05 · Head-cock at a loading bar (event-driven)</p>
            <div className="flex flex-wrap items-center gap-4">
              <button type="button" className={btn2Cls} onClick={() => fakeRequest(3200)}>
                Request · 3.2s
              </button>
              <button type="button" className={btn2Cls} onClick={() => fakeRequest(8000)}>
                Slow request · 8s
              </button>
              <button type="button" className={btn2Cls} onClick={() => fakeRequest(300)}>
                Fast request · 300ms
              </button>
              <div className="bg-canvas border border-border-subtle rounded-lg px-4 py-3 min-h-[76px] flex items-center">
                <OttoInlineLoading active={loadingDemo} message="Saving your changes" />
              </div>
            </div>
            <p className="font-['Metropolis'] text-[11px] text-ink-tertiary mt-2">
              Runs as long as the request does — in (450ms) → loop (1.8s, repeating) → out (450ms), unwinding from wherever the head actually is when the response lands, not from a canned pose. The <strong>300ms</strong> button should show <em>nothing at all</em>: under the ~600ms floor the whole scene is suppressed, because an intro chased straight by an outro reads as a flinch. His gaze is measured off the live bar rect, so it stays on the bar while the head turns.
            </p>
          </div>

          {showIntro && (
            <OttoIntroCard onShowAround={() => setShowIntro(false)} onDismiss={() => setShowIntro(false)} />
          )}
        </div>
      )}

      {/* Portals / one-shots */}
      <OttoAward show={award.show} label={award.label} onComplete={() => setAward({ show: false, label: null })} />
      {flight && <BriefSendFlight origin={flight} onComplete={() => setFlight(null)} />}
    </div>
  );
}

// The two registries, read live off the window helpers the roamer and OttoAlive
// publish — the SAME source the hidden `shotlist` panel uses, so this page can
// never list a stale set. A shot added to SHOTS appears here on its own.
//
// SURFACES THE REFUSAL. __ottoPlay returns a STRING when a shot cannot run
// ("no usable target on this screen"); the gallery's older buttons threw that
// away, which is exactly why a suppressed shot and a broken one looked
// identical here. The dev panel learned this lesson first — see OttoDevPanel.
function ShotBoard() {
  const [shots, setShots] = useState([]);
  const [motions, setMotions] = useState([]);
  const [result, setResult] = useState(null);

  // Polled rather than read once: the roamer mounts at the app root and may not
  // have published its helpers by the time this renders.
  useEffect(() => {
    const read = () => {
      setShots(Array.isArray(window.__ottoShots) ? window.__ottoShots : []);
      setMotions(Array.isArray(window.__ottoAnims) ? window.__ottoAnims : []);
    };
    read();
    const t = setInterval(read, 1000);
    return () => clearInterval(t);
  }, []);

  const run = (fn, id, okText) => {
    const call = window[fn];
    if (typeof call !== 'function') {
      setResult({ id, ok: false, text: 'Not available on this page — Otto only mounts for internal users.' });
      return;
    }
    const out = call(id);
    setResult(typeof out === 'string' ? { id, ok: false, text: out } : { id, ok: true, text: okText });
  };

  const pill = "font-['Metropolis'] text-[12px] text-ink-primary bg-surface-card border border-[#CFD2C8] rounded-full px-3 py-1.5 hover:border-ink-secondary transition-colors";

  return (
    <div>
      <p className={`${capCls} mb-2.5`}>Roaming shots — they travel across the page</p>
      {shots.length === 0 ? (
        <p className="font-['Metropolis'] text-[12px] text-ink-tertiary">
          No shots registered — the roamer mounts for internal users only.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {shots.map(id => (
            <button key={id} type="button" className={pill}
              onClick={() => run('__ottoPlay', id, 'Playing — watch the page, not this list.')}>
              {id}
            </button>
          ))}
        </div>
      )}
      <p className="font-['Metropolis'] text-[11px] text-ink-tertiary mt-2 max-w-[70ch]">
        These resolve against <strong>this page's</strong> cards and nav, not the ones they ship
        against. A shot that refuses here is usually right to — type <code>shotlist</code> on a real
        page to review them properly.
      </p>

      {motions.length > 0 && (
        <>
          <p className={`${capCls} mt-5 mb-2.5`}>Body motions — Otto moves where he stands</p>
          <div className="flex flex-wrap gap-2">
            {motions.map(kind => (
              <button key={kind} type="button" className={pill}
                onClick={() => run('__ottoAnim', kind, 'Playing on the interactive Otto below.')}>
                {kind}
              </button>
            ))}
          </div>
          <p className="font-['Metropolis'] text-[11px] text-ink-tertiary mt-2 max-w-[70ch]">
            Fires on the interactive Otto further down the page. <code>settle</code> is the quiet
            one — a dip, a held stillness, a release. If it reads as sorry rather than unbothered,
            it is wrong.
          </p>
        </>
      )}

      {result && (
        <p className={`font-['Metropolis'] text-[12px] mt-3 ${result.ok ? 'text-fusion-forest' : 'text-[#7A4E10]'}`}>
          <strong>{result.id}:</strong> {result.text}
        </p>
      )}
    </div>
  );
}
